import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Plus, MapPin, Trash2, ShieldCheck, ShieldOff, Edit2, X, Save, Upload, FileDown, Loader2 } from 'lucide-react';
import GeofenceMapDrawer from './GeofenceMapDrawer';
import FilterBar from './FilterBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  name: '', address: '', type: 'circle',
  center_lat: null, center_lon: null, radius_meters: 100,
  polygon_coordinates: null, buffer_meters: 100,
  gate_control_enabled: true,
};

export default function GeoFences() {
  const [fences, setFences] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [gateFilter, setGateFilter] = useState('all');

  const filteredFences = useMemo(() => {
    const q = search.trim().toLowerCase();
    return fences.filter(f => {
      if (typeFilter !== 'all' && f.type !== typeFilter) return false;
      const gateOn = f.gate_control_enabled ?? true;
      if (gateFilter === 'on' && !gateOn) return false;
      if (gateFilter === 'off' && gateOn) return false;
      if (q && !(
        (f.name || '').toLowerCase().includes(q) ||
        (f.address || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [fences, search, typeFilter, gateFilter]);

  useEffect(() => { fetchFences(); }, []);

  const fetchFences = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/geofences`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await r.json();
      setFences(data);
    } finally {
      setLoading(false);
    }
  };

  const startAdd = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setEditorOpen(true);
  };

  const startEdit = (f) => {
    setForm({
      name: f.name || '',
      address: f.address || '',
      type: f.type || 'circle',
      center_lat: f.center?.latitude ?? null,
      center_lon: f.center?.longitude ?? null,
      radius_meters: f.radius_meters ?? 100,
      polygon_coordinates: f.polygon_coordinates || null,
      buffer_meters: f.buffer_meters ?? 100,
      gate_control_enabled: f.gate_control_enabled ?? true,
    });
    setEditingId(f.id);
    setEditorOpen(true);
  };

  const closeEditor = () => { setEditorOpen(false); setForm(EMPTY_FORM); setEditingId(null); };

  const toggleGateControl = async (fence) => {
    const next = !(fence.gate_control_enabled ?? true);
    const msg = next
      ? `Enable gate control for "${fence.name}"? Staff breaks & visits will require security approval at the gate.`
      : `Disable gate control for "${fence.name}"? Staff will proceed without security approval — they'll just see a Break/Visit ID to write on the register and sign.`;
    if (!window.confirm(msg)) return;
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/geofences/${fence.id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ gate_control_enabled: next })
    });
    if (r.ok) fetchFences();
    else alert('Failed to update gate control');
  };

  const validateAndBuildBody = () => {
    if (!form.name.trim()) return { err: 'Name is required' };
    if (!form.address.trim()) return { err: 'Address is required' };
    const body = {
      name: form.name.trim(),
      address: form.address.trim(),
      type: form.type,
      buffer_meters: parseInt(form.buffer_meters || 100),
      gate_control_enabled: !!form.gate_control_enabled,
    };
    if (form.type === 'circle') {
      if (form.center_lat == null || form.center_lon == null) return { err: 'Draw the circle on the map' };
      body.center_lat = form.center_lat;
      body.center_lon = form.center_lon;
      body.radius_meters = parseInt(form.radius_meters || 100);
    } else {
      if (!form.polygon_coordinates || form.polygon_coordinates.length < 3) return { err: 'Polygon needs at least 3 vertices — click on the map' };
      body.polygon_coordinates = form.polygon_coordinates;
    }
    return { body };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { err, body } = validateAndBuildBody();
    if (err) { alert(err); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      let r;
      if (editingId) {
        // PUT expects: center as {latitude,longitude} object; normalise here
        const putBody = { ...body };
        if (body.type === 'circle') {
          putBody.center = { latitude: body.center_lat, longitude: body.center_lon };
          delete putBody.center_lat;
          delete putBody.center_lon;
        }
        r = await fetch(`${API_URL}/api/geofences/${editingId}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(putBody)
        });
      } else {
        r = await fetch(`${API_URL}/api/geofences`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      }
      if (r.ok) {
        closeEditor();
        fetchFences();
      } else {
        const text = await r.text();
        let data = {};
        try { data = text ? JSON.parse(text) : {}; } catch {}
        alert(data.detail || `Save failed (${r.status})`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this geo-fence?')) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/geofences/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    fetchFences();
  };

  // ===== Bulk import (Excel/CSV) =====
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');

  const downloadTemplate = () => {
    const headers = ['Name', 'Address', 'Type', 'Latitude', 'Longitude', 'Radius (m)',
                     'Polygon (lat,lon|lat,lon|...)', 'Buffer (m)', 'Gate Control'];
    const examples = [
      ['Carloo HO', 'S.F. No. 325/2B1, Tiruppur', 'circle', 11.057222, 77.340034, 100, '', 100, 'Yes'],
      ['Warehouse 2', 'Pirivu', 'polygon', '', '', '', '11.05,77.34|11.06,77.34|11.06,77.35|11.05,77.35', 100, 'No'],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...examples]);
    ws['!cols'] = [{ wch: 22 }, { wch: 32 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
                   { wch: 10 }, { wch: 40 }, { wch: 10 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'GEOFENCES');
    XLSX.writeFile(wb, 'geofences-template.xlsx');
  };

  const GF_ALIASES = {
    name: ['name', 'geofence', 'geo fence', 'geo-fence', 'location'],
    address: ['address', 'addr'],
    type: ['type', 'shape'],
    center_lat: ['latitude', 'lat', 'center lat', 'center_lat'],
    center_lon: ['longitude', 'lon', 'lng', 'center lon', 'center_lon'],
    radius_meters: ['radius (m)', 'radius', 'radius_meters', 'radius m'],
    polygon_coordinates: ['polygon', 'polygon (lat,lon|lat,lon|...)', 'polygon_coordinates', 'vertices'],
    buffer_meters: ['buffer (m)', 'buffer', 'buffer_meters', 'buffer m'],
    gate_control_enabled: ['gate control', 'gate_control', 'gate', 'gate_control_enabled'],
  };

  const pickCol = (obj, key) => {
    const aliases = GF_ALIASES[key] || [key];
    for (const k of Object.keys(obj || {})) {
      const lower = String(k).trim().toLowerCase();
      if (aliases.includes(lower)) return obj[k];
    }
    return undefined;
  };

  const parsePolygon = (raw) => {
    if (!raw) return null;
    if (Array.isArray(raw)) return raw;
    // Accept "lat,lon|lat,lon|..." or "lat,lon;lat,lon;..."
    const parts = String(raw).split(/[|;\n]+/).map(s => s.trim()).filter(Boolean);
    const pts = parts.map(p => p.split(',').map(x => parseFloat(x.trim())))
                     .filter(p => p.length === 2 && p.every(Number.isFinite));
    return pts.length >= 3 ? pts : null;
  };

  const parseBool = (raw) => {
    if (raw === true || raw === false) return raw;
    if (raw === undefined || raw === null || raw === '') return undefined;
    const s = String(raw).trim().toLowerCase();
    return ['yes', 'y', 'true', '1', 'on', 'enabled'].includes(s);
  };

  const normaliseGeofenceRows = (raw) => raw.map(r => {
    const type = String(pickCol(r, 'type') ?? 'circle').trim().toLowerCase();
    const row = {
      name: String(pickCol(r, 'name') ?? '').trim(),
      address: String(pickCol(r, 'address') ?? '').trim(),
      type: ['polygon', 'poly'].includes(type) ? 'polygon' : 'circle',
    };
    const lat = parseFloat(pickCol(r, 'center_lat'));
    const lon = parseFloat(pickCol(r, 'center_lon'));
    if (Number.isFinite(lat)) row.center_lat = lat;
    if (Number.isFinite(lon)) row.center_lon = lon;
    const rad = parseInt(pickCol(r, 'radius_meters'), 10);
    if (Number.isFinite(rad)) row.radius_meters = rad;
    const poly = parsePolygon(pickCol(r, 'polygon_coordinates'));
    if (poly) row.polygon_coordinates = poly;
    const buf = parseInt(pickCol(r, 'buffer_meters'), 10);
    if (Number.isFinite(buf)) row.buffer_meters = buf;
    const gate = parseBool(pickCol(r, 'gate_control_enabled'));
    if (gate !== undefined) row.gate_control_enabled = gate;
    return row;
  }).filter(r => r.name);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportMsg('');
    try {
      const ext = file.name.split('.').pop().toLowerCase();
      let raw;
      if (ext === 'csv') {
        const text = await file.text();
        const cleaned = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
        const wb = XLSX.read(cleaned, { type: 'string' });
        raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      } else {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      }
      const rows = normaliseGeofenceRows(raw);
      if (!rows.length) {
        setImportMsg('File empty or missing required Name column.');
        return;
      }
      if (!window.confirm(
        `Import ${rows.length} geo-fences?\n\n` +
        `• Existing geo-fences (matched by Name) will be UPDATED.\n` +
        `• Circles need Latitude + Longitude (+ Radius). Polygons need ≥3 vertices in the polygon column.\n` +
        `• Invalid rows are skipped and listed in the warnings.`
      )) return;
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/geofences/bulk-import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setImportMsg(d.detail || `Import failed (${r.status})`); return; }
      const warn = (d.warnings || []).slice(0, 5).join(' · ');
      setImportMsg(
        `Created ${d.created} · Updated ${d.updated} · ${d.skipped_invalid} skipped` +
        (warn ? ` — ${warn}${(d.warnings || []).length > 5 ? '…' : ''}` : '')
      );
      fetchFences();
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div data-testid="geofences-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Geo-Fences ({fences.length})</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={downloadTemplate}
            className="flex items-center gap-1 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            data-testid="geofence-template-btn"
            title="Download Excel template"
          >
            <FileDown size={16} /> Template
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-1 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 rounded-lg disabled:opacity-50"
            data-testid="geofence-import-btn"
            title="Import from Excel / CSV"
          >
            {importing ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            Import
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            data-testid="geofence-import-file"
            onChange={handleFile}
          />
          <button
            onClick={startAdd}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="add-geofence-btn"
          >
            <Plus size={20} /><span>Add Geo-Fence</span>
          </button>
        </div>
      </div>

      {importMsg && (
        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1 mb-4" data-testid="geofence-import-msg">
          {importMsg}
        </p>
      )}

      {editorOpen && (
        <GeofenceEditor
          form={form} setForm={setForm}
          editingId={editingId} saving={saving}
          onSubmit={handleSubmit} onClose={closeEditor}
        />
      )}

      <FilterBar
        testId="geofence-filters"
        placeholder="Search by name or address…"
        search={search}
        onSearch={setSearch}
        chips={[
          { key: 'type', label: 'Type', value: typeFilter, options: [
            { value: 'circle', label: 'Circle' }, { value: 'polygon', label: 'Polygon' }
          ]},
          { key: 'gate', label: 'Gate Control', value: gateFilter, options: [
            { value: 'on', label: 'On' }, { value: 'off', label: 'Off' }
          ]},
        ]}
        onChipChange={(k, v) => {
          if (k === 'type') setTypeFilter(v);
          else if (k === 'gate') setGateFilter(v);
        }}
        onClear={() => { setSearch(''); setTypeFilter('all'); setGateFilter('all'); }}
        resultCount={filteredFences.length}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredFences.length === 0 && (
          <div className="col-span-full text-center py-12 text-sm text-gray-500 bg-white rounded-xl border border-gray-100" data-testid="geofences-empty">
            {fences.length === 0 ? 'No geo-fences yet. Click "Add Geo-Fence" to create one.' : 'No geo-fences match the current filters.'}
          </div>
        )}
        {filteredFences.map(f => {
          const gateOn = f.gate_control_enabled ?? true;
          return (
            <div key={f.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-200" data-testid={`geofence-card-${f.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="p-2 bg-blue-100 rounded-lg"><MapPin className="text-blue-600" size={20} /></div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{f.name}</h3>
                    <p className="text-xs text-gray-500 mt-1">{f.address}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className="text-xs px-2 py-1 bg-gray-100 rounded">{f.type}</span>
                      {f.type === 'circle' && (
                        <span className="text-xs text-gray-600">{f.radius_meters}m + {f.buffer_meters || 100}m buffer</span>
                      )}
                      {f.type === 'polygon' && (
                        <span className="text-xs text-gray-600">{f.polygon_coordinates?.length || 0} vertices + {f.buffer_meters || 100}m buffer</span>
                      )}
                      <button
                        onClick={() => toggleGateControl(f)}
                        className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${gateOn ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}
                        data-testid={`gate-control-toggle-${f.id}`}
                      >
                        {gateOn ? <ShieldCheck size={12} /> : <ShieldOff size={12} />}
                        Gate: {gateOn ? 'ON' : 'OFF (register & sign)'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-1">
                  <button
                    onClick={() => startEdit(f)}
                    className="text-blue-600 p-1 hover:bg-blue-50 rounded"
                    data-testid={`edit-geofence-${f.id}`}
                    title="Edit"
                  ><Edit2 size={16} /></button>
                  <button
                    onClick={() => handleDelete(f.id)}
                    className="text-red-600 p-1 hover:bg-red-50 rounded"
                    data-testid={`delete-geofence-${f.id}`}
                    title="Delete"
                  ><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function GeofenceEditor({ form, setForm, editingId, saving, onSubmit, onClose }) {
  const updateShape = (patch) => setForm(p => ({ ...p, ...patch }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 overflow-y-auto" data-testid="geofence-editor">
      <div className="bg-white w-full max-w-3xl rounded-xl shadow-xl my-4 flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-lg">{editingId ? 'Edit Geo-Fence' : 'New Geo-Fence'}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X size={18} /></button>
        </div>

        <form onSubmit={onSubmit} className="overflow-y-auto p-5 space-y-3 flex-1">
          <input
            data-testid="geofence-name"
            placeholder="Name (e.g., Branch Office)"
            value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
            className="w-full px-4 py-2 border rounded-lg" required
          />
          <input
            data-testid="geofence-address"
            placeholder="Address"
            value={form.address} onChange={(e) => setForm({...form, address: e.target.value})}
            className="w-full px-4 py-2 border rounded-lg" required
          />

          <div className="flex items-center gap-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio" name="type" data-testid="type-circle"
                checked={form.type === 'circle'}
                onChange={() => setForm({...form, type: 'circle', polygon_coordinates: null})}
              />
              <span>Circle</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio" name="type" data-testid="type-polygon"
                checked={form.type === 'polygon'}
                onChange={() => setForm({...form, type: 'polygon', center_lat: null, center_lon: null})}
              />
              <span>Polygon (custom drawn)</span>
            </label>
          </div>

          <GeofenceMapDrawer
            key={form.type + (editingId || 'new')}
            type={form.type}
            value={form}
            onChange={updateShape}
            height={340}
          />

          {form.type === 'circle' && form.center_lat != null && (
            <div className="grid grid-cols-3 gap-2 text-xs bg-gray-50 rounded p-2">
              <div><span className="text-gray-500">Lat:</span> <span className="font-mono">{form.center_lat.toFixed(6)}</span></div>
              <div><span className="text-gray-500">Lng:</span> <span className="font-mono">{form.center_lon.toFixed(6)}</span></div>
              <div><span className="text-gray-500">Radius:</span> <span className="font-semibold">{form.radius_meters}m</span></div>
            </div>
          )}
          {form.type === 'polygon' && form.polygon_coordinates?.length > 0 && (
            <div className="text-xs bg-gray-50 rounded p-2">
              <span className="text-gray-500">Vertices:</span> <span className="font-semibold">{form.polygon_coordinates.length}</span>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-gray-600">Buffer (meters - extra tolerance)</label>
            <input
              type="number" value={form.buffer_meters}
              onChange={(e) => setForm({...form, buffer_meters: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg"
              data-testid="geofence-buffer"
            />
          </div>

          <label className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={form.gate_control_enabled}
              onChange={(e) => setForm({...form, gate_control_enabled: e.target.checked})}
              className="mt-1"
              data-testid="geofence-gate-control-toggle"
            />
            <span className="text-sm">
              <span className="font-semibold text-blue-900">Gate control required</span>
              <span className="block text-xs text-blue-700 mt-0.5">
                {form.gate_control_enabled
                  ? 'Staff breaks & visits will need security approval at the gate before exit/entry.'
                  : 'Staff will proceed freely — they will see a Break/Visit ID to write on the register and sign.'}
              </span>
            </span>
          </label>
        </form>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancel</button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg"
            data-testid="geofence-submit"
          >
            <Save size={16} />
            {saving ? 'Saving…' : (editingId ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}
